import { getPreferredProduct } from '__core/shared/util/salestrekker';
import template from './summary.template.html';

export default {
  template,
  bindings: {
    formName: '<',
    lenders: '<',
    documents: '<',
    validationErrors: '<',
    contacts: '<',
    assets: '<',
    liabilities: '<',
    homeLoanSecurities: '<',
    reviewProducts: '<',
    compareProducts: '<',
    switchProducts: '<',
    compliance: '<',
    objectives: '<',
    fundingWorksheets: '<',
    workflow: '<',
    settledTimeStamp: '<',
    idTicket: '<',
    isTicketSettled: '<',
    onChanged: '&',
    onFinished: '&'
  },
  controller: class controller {
    constructor(
      $rootScope,
      $timeout,
      $mdUtil,
      $mdMedia,
      helperService,
      financesService,
      incomeService,
      householdsService,
      assetsService,
      liabilitiesService,
      homeLoanService,
      ReviewProduct,
      CompareProduct,
      HomeLoanCompliance,
      OwnershipType,
      ZoningOption,
      TransactionType
    ) {
      'ngInject';

      /*
       * Controller Model
       * --------------------------------------------------------------------------------
       */
      const ctrl = this;
      ctrl.mdMedia = $mdMedia;
      ctrl.currentOrganization = $rootScope.CurrentOrganization;
      ctrl.currentDateFormat = $rootScope.CurrentDateFormatPrimary();
      ctrl.currentCurrency = $rootScope.CurrentCurrency();
      ctrl.organizationCountry = '';
      ctrl.availableZoningOptions = [];
      ctrl.availableOwnershipTypes = [];
      ctrl.existingInvestmentProperties = [];
      ctrl.existingOwnerOccupiedProperties = [];
      ctrl.preferredLender = null;
      ctrl.isProcessing = true;
      ctrl.isReady = false;

      ctrl.initialize = () => {
        ctrl.organizationCountry = helperService.getOrganizationCountry();
        ctrl.currentCurrency = $rootScope.CurrentCurrency();
        ctrl.availableZoningOptions = ZoningOption.available();
        ctrl.availableOwnershipTypes = OwnershipType.available();
        ctrl.lenders = ctrl.lenders || [];
        ctrl.documents = ctrl.documents || [];
        ctrl.validationErrors = ctrl.validationErrors || [];
        ctrl.contacts = ctrl.contacts || [];
        ctrl.assets = ctrl.assets || [];
        ctrl.liabilities = ctrl.liabilities || [];
        ctrl.homeLoanSecurities = ctrl.homeLoanSecurities || [];
        ctrl.reviewProducts = ctrl.reviewProducts || [];
        ctrl.compareProducts = ctrl.compareProducts || [];
        ctrl.switchProducts = ctrl.switchProducts || [];
        ctrl.fundingWorksheets = ctrl.fundingWorksheets || {};
        ctrl.compliance = ctrl.compliance || {};
        ctrl.objectives = ctrl.objectives || {};
        ctrl.workflow = ctrl.workflow || {};
        ctrl.settledTimeStamp = ctrl.settledTimeStamp || 0;
        ctrl.idTicket = ctrl.idTicket || '';
        ctrl.isTicketSettled = ctrl.isTicketSettled || false;
        ctrl.preferredProduct = getPreferredProduct(ctrl.compareProducts);
        ctrl.preferredLender = ctrl.preferredProduct
          ? ctrl.getLender(ctrl.preferredProduct.idLender)
          : null;
        ctrl.charts = {
          netAssetPosition: {
            values: [],
            height: '100%',
            width: '100%',
            legends: [],
            colors: ['#03A9F4'],
            symbol: ctrl.currentCurrency?.symbol,
            isOut: true
          },
          monthlyFinancialPosition: {
            values: [],
            height: '100%',
            width: '100%',
            legends: [],
            colors: ['#03A9F4'],
            symbol: ctrl.currentCurrency?.symbol,
            isOut: true
          },
          financialRiskIndicator: {
            minValue: 0,
            maxValue: 100,
            value: 0,
            height: '100%',
            width: '100%',
            colors: ['#4CAF50', '#FF9800', '#F44336'],
            legends: [{ name: '-' }],
            ranges: [0.25, 0.6]
          },
          financialStressIndicator: {
            minValue: 0,
            maxValue: 100,
            value: 0,
            height: '100%',
            width: '100%',
            colors: ['#4CAF50', '#FF9800', '#F44336'],
            legends: [{ name: '-' }],
            ranges: [0.15, 0.35, 0.6]
          },
          hemVariance: {
            minValue: 0,
            maxValue: 100,
            value: 0,
            height: '100%',
            width: '100%',
            colors: ['#4CAF50', '#F44336', '#F44336'],
            legends: [{ name: '-' }],
            ranges: [0, 0.01]
          },
          debtToIncomeRatio: {
            minValue: 0,
            maxValue: 15,
            value: 0,
            height: '100%',
            width: '100%',
            colors: ['#4CAF50', '#FF9800', '#F44336'],
            legends: [{ name: '-' }],
            ranges: [0.2, 0.47]
          },
          lvrData: {
            values: [],
            isStack: true,
            height: '100%',
            width: '100%',
            legends: [],
            colors: ['#03A9F4', '#FF9800'],
            symbol: ctrl.currentCurrency?.symbol
          },
          loanAmortizationData: {
            values: [],
            height: '100%',
            width: '100%',
            legends: [],
            colors: ['#03A9F4', '#FF9800'],
            symbol: ctrl.currentCurrency?.symbol
          },
          isNetAssetPositionReady: false,
          isMonthlyFinancialPositionReady: false,
          isFinancialRiskIndicatorReady: false,
          isFinancialStressIndicatorReady: false,
          isHemVarianceReady: false,
          isDebtToIncomeRatioReady: false,
          isLvrReady: false,
          isLoanReady: false
        };

        if (ctrl.organizationCountry.cca2 !== 'NZ') {
          ctrl.isProcessing = true;
          ctrl.financeMonthlyFinancialPositionEstimate();
        } else {
          ctrl.summaryCharts();
        }

        ctrl.isReady = true;
      };

      ctrl.financeMonthlyFinancialPositionEstimate = () => {
        financesService
          .monthlyFinancialPosition({ idTicket: ctrl.idTicket || '' })
          .then((res) => {
            if (res?.status && !_.isEmpty(res?.compliance)) {
              ctrl.compliance = HomeLoanCompliance.Build(res?.compliance);
              const data = {
                compliance: ctrl.compliance
              };
              ctrl.onChanged({ data });
            }
            ctrl.isProcessing = false;
            ctrl.summaryCharts();
          });
      };

      ctrl.summaryCharts = () => {
        if (ctrl.organizationCountry.cca2 !== 'NZ') {
          // Net asset position
          const totalAssets = ctrl.compliance?.monthlyFinancialPosition?.totalAssets || 0;
          const totalLiabilities =
            ctrl.compliance?.monthlyFinancialPosition?.totalLiabilities || 0;
          const netAssetPosition =
            ctrl.compliance?.monthlyFinancialPosition?.netAssetPosition || 0;
          ctrl.charts.netAssetPosition.values.push(
            {
              name: 'Net asset position',
              value: [netAssetPosition],
              color: ['#4CAF50', '#F44336']
            },
            {
              name: 'Total liabilities',
              value: [totalLiabilities]
            },
            {
              name: 'Total assets',
              value: [totalAssets]
            }
          );
          $timeout(() => {
            ctrl.charts.isNetAssetPositionReady = true;
          });

          // Monthly financial position
          const netMonthlyIncome =
            ctrl.compliance?.monthlyFinancialPosition?.netIncome?.proposedPosition || 0;
          const totalOutgoings = ctrl.compliance?.monthlyFinancialPosition?.totalOutgoings || 0;
          const netCashPosition =
            ctrl.compliance?.monthlyFinancialPosition?.netCashPosition || 0;
          ctrl.charts.monthlyFinancialPosition.values.push(
            {
              name: 'Net cash position',
              value: [netCashPosition],
              color: ['#4CAF50', '#F44336']
            },
            {
              name: 'Total outgoings',
              value: [totalOutgoings]
            },
            {
              name: 'Net monthly income',
              value: [netMonthlyIncome]
            }
          );
          $timeout(() => {
            ctrl.charts.isMonthlyFinancialPositionReady = true;
          });

          // Financial risk indicator
          ctrl.charts.financialRiskIndicator.value =
            ctrl.compliance?.monthlyFinancialPosition?.financialRisk || 0;
          if (ctrl.compliance?.monthlyFinancialPosition?.financialRisk <= 25) {
            ctrl.charts.financialRiskIndicator.legends[0] = { name: 'Low' };
          }
          if (
            ctrl.compliance?.monthlyFinancialPosition?.financialRisk > 25 &&
            ctrl.compliance?.monthlyFinancialPosition?.financialRisk <= 60
          ) {
            ctrl.charts.financialRiskIndicator.legends[0] = { name: 'Medium' };
          }
          if (
            ctrl.compliance?.monthlyFinancialPosition?.financialRisk > 60 &&
            ctrl.compliance?.monthlyFinancialPosition?.financialRisk <= 100
          ) {
            ctrl.charts.financialRiskIndicator.legends[0] = { name: 'High' };
          }
          $timeout(() => {
            ctrl.charts.isFinancialRiskIndicatorReady = true;
          });

          // Financial stress indicator
          if (
            ctrl.compliance?.monthlyFinancialPosition?.surplusDeficit
              ?.proposedPositionAtBufferRate <= 0
          ) {
            ctrl.charts.financialStressIndicator.value = 60;
            ctrl.charts.financialStressIndicator.legends[0] = { name: 'High' };
          }
          if (
            ctrl.compliance?.monthlyFinancialPosition?.surplusDeficit
              ?.proposedPositionAtBufferRate > 0 &&
            ctrl.compliance?.monthlyFinancialPosition?.surplusDeficit
              ?.proposedPositionAtBufferRate < 1000
          ) {
            ctrl.charts.financialStressIndicator.value = 35;
            ctrl.charts.financialStressIndicator.legends[0] = { name: 'Medium' };
          }
          if (
            ctrl.compliance?.monthlyFinancialPosition?.surplusDeficit
              ?.proposedPositionAtBufferRate >= 1000
          ) {
            ctrl.charts.financialStressIndicator.value = 15;
            ctrl.charts.financialStressIndicator.legends[0] = { name: 'Low' };
          }
          $timeout(() => {
            ctrl.charts.isFinancialStressIndicatorReady = true;
          });

          // HEM variance
          const hemVariance = ctrl.compliance?.monthlyFinancialPosition?.hemVariance || 0;
          ctrl.charts.hemVariance.value = hemVariance
            ? _.multiply(_.divide(hemVariance, -3000), 100)
            : 0;
          if (ctrl.charts.hemVariance.value > 100) {
            ctrl.charts.hemVariance.value = 100;
          }
          if (ctrl.charts.hemVariance.value <= 0) {
            ctrl.charts.hemVariance.value = 0;
          }
          ctrl.charts.hemVariance.legends[0] = {
            name: `${hemVariance.formatWithCurrency($rootScope.CurrentCurrency())}`
          };
          $timeout(() => {
            ctrl.charts.isHemVarianceReady = true;
          });

          // Debt to income ration (DTI)
          ctrl.charts.debtToIncomeRatio.value =
            ctrl.compliance?.monthlyFinancialPosition?.debtToIncomeRatio || 0;
          ctrl.charts.debtToIncomeRatio.legends[0] = {
            name: `${ctrl.charts.debtToIncomeRatio.value}`
          };
          $timeout(() => {
            ctrl.charts.isDebtToIncomeRatioReady = true;
          });
        }
        // Loan amortization chart
        if (ctrl?.preferredProduct?.productSplits?.length) {
          const principalBalances = [];
          const principalOffsetBalances = [];
          let currentYear = ctrl.settledTimeStamp
            ? _.add(parseInt(moment.utc(ctrl.settledTimeStamp, 'X').year(), 10), 1)
            : _.add(parseInt(moment.utc().year(), 10), 1);

          const pB = _.map(ctrl.preferredProduct.productSplits, 'principalBalances');
          const maxPeriod = _.maxBy(pB, (p) => p?.length || 0);
          maxPeriod.push(0);
          if (pB?.length) {
            for (let a = 0; a < pB?.length; a += 1) {
              for (let b = 0; b < maxPeriod?.length; b += 1) {
                if (!principalBalances[b]) {
                  principalBalances[b] = 0;
                }
                principalBalances[b] += pB[a][b] && pB[a][b] > 0 ? Math.round(pB[a][b]) : 0;
              }
            }
          }
          const pOB = _.map(ctrl.preferredProduct.productSplits, 'principalOffsetBalances');
          const maxOffsetPeriod = _.maxBy(pOB, (p) => p?.length || 0);
          if (pOB?.length) {
            for (let a = 0; a < pOB?.length; a += 1) {
              for (let b = 0; b < maxOffsetPeriod?.length; b += 1) {
                if (!principalOffsetBalances[b]) {
                  principalOffsetBalances[b] = 0;
                }
                principalOffsetBalances[b] +=
                  pOB[a][b] && pOB[a][b] > 0 ? Math.round(pOB[a][b]) : 0;
              }
            }
          }
          for (let i = 0; i < principalBalances?.length; i += 1) {
            if (principalOffsetBalances?.length) {
              ctrl.charts.loanAmortizationData.values.push({
                name: currentYear.toString(),
                value: [principalBalances[i], principalOffsetBalances[i] || 0]
              });
            } else {
              ctrl.charts.loanAmortizationData.values.push({
                name: currentYear.toString(),
                value: [principalBalances[i]]
              });
            }
            currentYear += 1;
          }
          if (principalOffsetBalances?.length) {
            ctrl.charts.loanAmortizationData.legends.push(
              {
                name: 'Without offset'
              },
              {
                name: 'With offset'
              }
            );
          }
          $timeout(() => {
            ctrl.charts.isLoanReady = true;
          });
        }

        // LVR chart
        const loanAmount = _.reduce(
          ctrl.fundingWorksheets?.fundsAvailable || [],
          (memo, num) => _.add(memo, _.toNumber(num?.totalProposedLoanAmount || 0)),
          0
        );
        let remainingEquity = 0;
        if (
          ctrl.fundingWorksheets?.fundsRequired?.length === 1 &&
          ctrl.fundingWorksheets?.fundsAvailable?.length === 1
        ) {
          const fundRequired = ctrl.fundingWorksheets?.fundsRequired[0];
          if (_.toNumber(fundRequired?.purposeFunds || 0) > 0) {
            const fundAvailable = ctrl.fundingWorksheets?.fundsAvailable[0];
            switch (fundRequired?.transactionType?.name) {
              case TransactionType?.purchase?.name:
                remainingEquity = helperService.round({
                  value: _.subtract(
                    _.toNumber(fundRequired?.purposeFunds),
                    _.toNumber(fundAvailable?.totalProposedLoanAmount || 0)
                  )
                });
                break;
              case TransactionType?.refinance?.name:
                remainingEquity = helperService.round({
                  value: _.subtract(
                    _.toNumber(fundRequired?.purposeFunds),
                    _.toNumber(fundAvailable?.totalProposedLoanAmount || 0) >
                      _.toNumber(fundRequired?.existingLoanBalance || 0)
                      ? _.toNumber(fundAvailable?.totalProposedLoanAmount || 0)
                      : _.toNumber(fundRequired?.existingLoanBalance || 0)
                  )
                });
                break;
              case TransactionType?.top_up_or_increase?.name:
              case TransactionType?.equity_release?.name: {
                remainingEquity = helperService.round({
                  value:
                    _.toNumber(fundRequired?.purposeFunds) -
                    _.toNumber(fundAvailable?.totalProposedLoanAmount || 0) -
                    _.toNumber(fundRequired?.existingLoanBalance || 0) -
                    _.toNumber(fundRequired?.otherLoansAgainstSecurity || 0)
                });
                break;
              }
              default:
                remainingEquity = 0;
                break;
            }
          }
        }
        if (
          ctrl.fundingWorksheets?.fundsRequired?.length > 1 &&
          ctrl.fundingWorksheets?.fundsAvailable?.length > 1
        ) {
          let totalPurposeFunds = 0;
          let totalOtherLoansAgainstSecurity = 0;
          let totalExistingLoanBalanceOfTopUpAndEquity = 0;
          let totalExistingLoanBalanceOfRefinance = 0;
          let totalProposedLoanAmount = 0;
          for (let i = 0; i < ctrl.fundingWorksheets?.fundsRequired?.length; i += 1) {
            const fundRequired = ctrl.fundingWorksheets?.fundsRequired[i];
            const fundAvailable = ctrl.fundingWorksheets?.fundsAvailable[i];

            totalPurposeFunds += _.toNumber(fundRequired?.purposeFunds || 0);
            totalOtherLoansAgainstSecurity += _.toNumber(
              fundRequired?.otherLoansAgainstSecurity || 0
            );
            if (fundRequired?.transactionType?.name === TransactionType?.purchase?.name) {
              totalProposedLoanAmount += _.toNumber(
                fundAvailable?.totalProposedLoanAmount || 0
              );
            }
            if (fundRequired?.transactionType?.name === TransactionType?.refinance?.name) {
              totalExistingLoanBalanceOfRefinance +=
                _.toNumber(fundAvailable?.totalProposedLoanAmount || 0) >
                _.toNumber(fundRequired?.existingLoanBalance || 0)
                  ? _.toNumber(fundAvailable?.totalProposedLoanAmount || 0)
                  : _.toNumber(fundRequired?.existingLoanBalance || 0);
            }
            if (
              fundRequired?.transactionType?.name ===
                TransactionType?.top_up_or_increase?.name ||
              fundRequired?.transactionType?.name === TransactionType?.equity_release?.name
            ) {
              totalExistingLoanBalanceOfTopUpAndEquity += _.toNumber(
                fundRequired?.existingLoanBalance || 0
              );
            }
          }
          remainingEquity = helperService.round({
            value:
              totalPurposeFunds -
              totalOtherLoansAgainstSecurity -
              totalExistingLoanBalanceOfTopUpAndEquity -
              totalExistingLoanBalanceOfRefinance -
              totalProposedLoanAmount
          });
        }
        if (
          ctrl.fundingWorksheets?.fundsRequired?.length > 1 &&
          ctrl.fundingWorksheets?.fundsAvailable?.length === 1
        ) {
          let totalPurposeFunds = 0;
          let totalOtherLoansAgainstSecurity = 0;
          let totalExistingLoanBalanceOfTopUpAndEquity = 0;
          let totalExistingLoanBalanceOfRefinance = 0;
          const fundAvailable = ctrl.fundingWorksheets?.fundsAvailable[0];
          const totalProposedLoanAmount = _.toNumber(
            fundAvailable?.totalProposedLoanAmount || 0
          );
          for (let i = 0; i < ctrl.fundingWorksheets?.fundsRequired?.length; i += 1) {
            const fundRequired = ctrl.fundingWorksheets?.fundsRequired[i];

            totalPurposeFunds += _.toNumber(fundRequired?.purposeFunds || 0);
            totalOtherLoansAgainstSecurity += _.toNumber(
              fundRequired?.otherLoansAgainstSecurity || 0
            );
            if (fundRequired?.transactionType?.name === TransactionType?.refinance?.name) {
              totalExistingLoanBalanceOfRefinance += _.toNumber(
                fundRequired?.existingLoanBalance || 0
              );
            }
            if (
              fundRequired?.transactionType?.name ===
                TransactionType?.top_up_or_increase?.name ||
              fundRequired?.transactionType?.name === TransactionType?.equity_release?.name
            ) {
              totalExistingLoanBalanceOfTopUpAndEquity += _.toNumber(
                fundRequired?.existingLoanBalance || 0
              );
            }
          }
          remainingEquity = helperService.round({
            value:
              totalPurposeFunds -
              totalOtherLoansAgainstSecurity -
              totalExistingLoanBalanceOfTopUpAndEquity -
              (totalProposedLoanAmount > totalExistingLoanBalanceOfRefinance
                ? totalProposedLoanAmount
                : totalExistingLoanBalanceOfRefinance)
          });
        }

        const maxLvr = !_.isEmpty(ctrl.fundingWorksheets?.fundsAvailable)
          ? _.maxBy(ctrl.fundingWorksheets?.fundsAvailable, (f) => f?.totalLvr || 0)
          : {};
        if (maxLvr?.totalLvr > 80) {
          ctrl.charts.lvrData.colors = ['#03A9F4', '#F44336'];
        }
        if (maxLvr?.totalLvr < 80) {
          ctrl.charts.lvrData.colors = ['#03A9F4', '#4CAF50'];
        }
        if (loanAmount > 0 && remainingEquity > 0) {
          ctrl.charts.lvrData.values = [{ name: '', value: [loanAmount, remainingEquity] }];
          ctrl.charts.lvrData.legends = [
            {
              name: 'Loan amount',
              value: [`${loanAmount.formatWithCurrency($rootScope.CurrentCurrency())}`]
            },
            {
              name: 'Remaining equity',
              value: [`${remainingEquity.formatWithCurrency($rootScope.CurrentCurrency())}`]
            }
          ];
          $timeout(() => {
            ctrl.charts.isLvrReady = true;
          });
        }
      };

      ctrl.getProduct = (idLender, idx) => {
        let selectedProduct;
        if (ctrl.reviewProducts.length) {
          selectedProduct = ctrl.reviewProducts.find((p) => p.idLender === idLender);
        }
        if (selectedProduct) {
          return selectedProduct.productSplits[idx];
        }
        return selectedProduct;
      };

      ctrl.getLender = (idLender) => _.find(ctrl.lenders || [], (l) => l.id === idLender);

      ctrl.getLenderDocument = (idLender) => {
        const doc = _.find(
          ctrl.documents || [],
          (d) => d?.lender[0]?.id === idLender && _.indexOf(d?.type, 'LOGO') > -1
        );
        return doc?.url || '';
      };

      ctrl.saveSummary = _.debounce(() => {
        const data = {
          reviewProducts: ctrl.reviewProducts
        };
        ctrl.onFinished({ data });
      }, 2000);

      ctrl.$onInit = () => {
        ctrl.initialize();
      };
    }
  }
};
